import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { requests } from "../../services/http-common";
import { useEffect, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { EmployeeRoles } from "../../models/Employee";
import { LabelValue } from "../../components/LabelValue/LabelValue";
import { Calendar } from "primereact/calendar";
import moment from "moment";

import styles from "./Reports.module.scss";
import { classNames as cx } from "primereact/utils";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Loader } from "../../components/Loader/Loader";
import { Button } from "primereact/button";
import { useToast } from "../../Providers/ToastProvider";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import { ReportId, reportsMapping } from "../../constants/constants";

interface TimeAnalysis {
  employeeFullName: string;
  customerName: string;
  dateIn: string;
  inOut: string;
  totalHours: string;
}
interface WorkforceDashboard {
  fullName: string;
  Mon: string;
  Tue: string;
  Wed: string;
  Thu: string;
  Fri: string;
  Sat: string;
  Sun: string;
  Total: string;
}

export function Reports() {
  const { reportId } = useParams<{ reportId: ReportId }>();
  const [pdf, setPdf] = useState<any>();
  const [csv, setCsv] = useState<any>();
  const [workforceData, setWorkforceData] = useState<WorkforceDashboard[] | null>(null);
  const [dataTable, setDataTable] = useState<TimeAnalysis[] | null>(null);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [employee, setEmployee] = useState<any>("all");
  const [workforceStartDate, setWorkforceStartDate] = useState<Date>();

  const { show } = useToast();

  const isAnalysis = reportId === ReportId.TimeAnalysis;
  const isWorkforce = reportId ===ReportId.WorkforceDashboard;

  const getLastMonday = (date: Date): Date => {
    const dayOfWeek = date.getDay();
    const daysUntilLastMonday = (dayOfWeek + 6) % 7;
    const lastMonday = new Date(date);
    lastMonday.setDate(date.getDate() - daysUntilLastMonday);
    return lastMonday;
  };

  const {
    error,
    isLoading,
    mutate: postReport,
  } = useMutation({
    mutationFn: (data: any) => {
      const { newStartDate, newEndDate, newEmployee } = data;
      const startTime = newStartDate || startDate;
      const endTime = newEndDate || endDate;
      const employee_ = newEmployee || employee;
      return requests.post(
        employee_ === "all"
          ? `/reporting/${reportId}`
          : `/reporting/${reportId}/${employee_.id}`,
        {
          startTime,
          endTime,
          utcOffset: moment().utcOffset(),
        },
        {
          ...(!isAnalysis
            ? {
                headers: { "Content-Type": "multipart/form-data" },
                responseType: "arraybuffer",
              }
            : {}),
        }
      );
    },
    onSuccess: (data) => {
      if (isAnalysis) {
        setDataTable(data.timeAnalyses);
      } else {
        const blob = new Blob([data], { type: "application/pdf" });
        setPdf(blob);
      }
    },
    retry: false,
  });

  const { mutate: postTimeAnalysis } = useMutation({
    mutationFn: () => {
      return requests.post(
        employee === "all"
          ? `/reporting/time-analysis-csv`
          : `/reporting/time-analysis-csv/${employee.id}`,
        {
          startTime: startDate,
          endTime: endDate,
          utcOffset: moment().utcOffset(),
        },
        {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "arraybuffer",
        }
      );
    },
    onSuccess: (data: any) => {
      const blob = new Blob([data], { type: "application/csv" });
      setCsv(blob);
    },
    retry: false,
  });

  const {
    isLoading: workforceIsLoading,
    mutate: postWorkforceDashboard,
  } = useMutation({
    mutationFn: () => {
      const lastMonday = workforceStartDate ? workforceStartDate : getLastMonday(new Date())
      return requests.post(
        `/reporting/workforce`,
        {
          startTime: lastMonday,
        }
      );
    },
    onSuccess: (data: any) => {
      const mappedData = data.map((item: any) => {
        const { fullName, weekly } = item;
        const total = item.total || null; 
        return { fullName, ...weekly, total };
      });
      setWorkforceData(mappedData);
    },
    retry: false,
  });

  useEffect(() => {
    const today = new Date();
    const lastMonday = getLastMonday(today);
    setWorkforceStartDate(lastMonday);
  }, []);

  useEffect(() => {
    if (isAnalysis && startDate && endDate) {
      postTimeAnalysis();
    }
  }, [employee, startDate, endDate]);
    
  useEffect(() => {
    if (isWorkforce && workforceStartDate) {
      postWorkforceDashboard();
    }
  }, [workforceStartDate]);

  const { data: employees } = useQuery({
    queryKey: ["users"],
    queryFn: () => {
      return requests.get(
        `/user?filterByRole=${EmployeeRoles.CLEANING_TECHNICIAN},${EmployeeRoles.SUPERVISOR}&rows=300`
      );
    },
    select: (data) => {
      const modified = data.result
        .map((e: any) => ({
          label: `${e.firstName} ${e.lastName}`,
          value: e,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      return [{ label: "Show All", value: "all" }, ...modified];
    },
    refetchOnWindowFocus: false,
  });

  const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  const columns = [
    { field: 'fullName', header: 'Full Name' },
    ...weekdays.map((day) => ({ field: day, header: day })),
    { field: 'total', header: 'Total' },
  ];

  const renderReport = useMemo(() => {
    if ((error as any)?.response?.status === 400) {
      show({
        severity: "error",
        detail: new TextDecoder().decode((error as any)?.response.data),
      });
    }

    if (isLoading || workforceIsLoading) {
      return <Loader />;
    }

    if (error) {
      return <h2 className={styles.errorMessage}>No Reports Found</h2>;
    }

    if (isAnalysis) {
      return (
        <DataTable
          value={dataTable as any}
          style={{ width: "100%", height: "100%", overflow: "auto" }}
        >
          <Column field="customerName" header="Customer Name" />
          <Column field="dateIn" header="Date In" />
          <Column field="employeeFullName" header="Employee Full Name" />
          <Column field="totalHours" header="Hours" />
          <Column field="inOut" header="In Out" />
        </DataTable>
      );
    }

    if (pdf) {
      return (
        <embed
          src={URL.createObjectURL(pdf)}
          type="application/pdf"
          className={styles.report}
        />
      );
    }

    if (isWorkforce) {
      return (
        <DataTable
          value={workforceData as any}
          style={{ width: '100%', height: '100%', overflow: 'auto' }}
        >
          {columns.map((column) => (
            <Column key={column.field} field={column.field} header={column.header} />
          ))}
        </DataTable>
      )   
    }

    return null;
  }, [pdf, error, dataTable, reportId, isLoading, workforceIsLoading]);

  useEffect(() => {
    const subtractConfigs = {
      count: reportId === ReportId.JobActualVsSchedule ? 6 : 1,
      type: reportId === ReportId.JobActualVsSchedule ? "days" : "months",
    };

    const newStartDate = moment()
      .subtract(subtractConfigs.count as any, subtractConfigs.type)
      .startOf("day")
      .toDate();
    const newEndDate = moment().endOf("day").toDate();

    setPdf(null);
    setDataTable(null);
    !isWorkforce && postReport({ newEmployee: "all", newStartDate, newEndDate });
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setEmployee("all");
  }, [reportId]);

  const isMonday = (date: Date): boolean => date.getDay() === 1;

  
  const getDisabledDates = (): Date[] => {
    const today = new Date();
    const startDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    const endDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());

    const disabledDates: Date[] = [];
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      if (!isMonday(currentDate)) {
        disabledDates.push(new Date(currentDate));
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return disabledDates;
  };

  const disabledDates = getDisabledDates();


  const formatRange = (startDate: Date, endDate: Date): string => {
    const startDateString = `${startDate.getMonth() + 1}/${startDate.getDate()}/${startDate.getFullYear()}`;
    const endDateString = `${endDate.getMonth() + 1}/${endDate.getDate()}/${endDate.getFullYear()}`;
    return `${startDateString}-${endDateString}`;
  };

  const calculateUpcomingSundayRange = (mondayDate: Date): string => {
    const upcomingSunday = new Date(mondayDate);
    upcomingSunday.setDate(mondayDate.getDate() + 6);

    return formatRange(mondayDate, upcomingSunday);
  };

  const sundayRange = useMemo(() => {
    if (workforceStartDate) {
      return calculateUpcomingSundayRange(workforceStartDate);
    }
    return '';
  }, [workforceStartDate]);

  return (
    <>
    <SectionHeader title={reportId && reportsMapping[reportId]} />
    { isWorkforce && workforceData ? 
      <>
        <LabelValue label="Select Date" bold className={styles.startDate}>
          <Calendar
            value={workforceStartDate}
            onChange={(e: any) => 
            {
              const newStartDate = moment(e.value)
              .set({
                hour: 12,
                minute: 34,
                second: 56,
              })
              .toDate();
              setWorkforceStartDate(newStartDate)
            }}
            disabledDates={disabledDates}
          />
        </LabelValue>
        <div>
          <p>Time Period: {sundayRange}</p>
        </div>
        <div className={styles.reportContainerWorkforce}>{renderReport}</div>
      </>
    : 
     <> 
      <div className={styles.header}>
        {reportId !== ReportId.JobSummary ? (
          <LabelValue label="Employees" bold>
            <Dropdown
              appendTo="self"
              options={employees}
              ariaLabel="name"
              value={employee}
              onChange={(e: any) => {
                postReport({});
                setEmployee(e.value);
              }}
              style={{ margin: "5px 0" }}
            />
          </LabelValue>
        ) : (
          <div />
        )}
        <div className={styles.dates}>
          {isAnalysis && dataTable && csv ? (
            <a
              href={URL.createObjectURL(csv)}
              download="report-time-analysis.csv"
            >
              <Button
                icon="pi pi-download"
                className={cx(
                  "p-button-text p-button-secondary",
                  styles.downloadButton
                )}
              />
            </a>
          ) : null}
          <LabelValue label="Start Date" bold>
            <Calendar
              value={startDate}
              onChange={(e: any) => {
                const newStartDate = moment(e.value).startOf("day").toDate();
                postReport({ newStartDate });
                setStartDate(newStartDate);
              }}
            />
          </LabelValue>
          <LabelValue label="End Date" bold>
            <Calendar
              value={endDate}
              onChange={(e: any) => {
                const newEndDate = moment(e.value).endOf("day").toDate();

                postReport({ newEndDate });
                setEndDate(newEndDate);
              }}
            />
          </LabelValue>
        </div>
      </div>
      <div className={styles.reportContainer}>{renderReport}</div>
     </>
    }
      
    </>
  );
}
